import dayjs from 'dayjs';
import { createPortal } from 'react-dom';

import React, { useRef, useState } from 'react';
import FeedCenterStatisticsFeedWritingPercentIcon from './Icon/FeedCenterStatisticsFeedWritingPercentIcon';
import FeedCenterStatisticsGoodPercent from './Icon/FeedCenterStatisticsGoodPercentIcon';
import FeedCenterStatisticsReregister from './Icon/FeedCenterStatisticsReregister';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import { isZeroOrDefined } from '@/lib/utils';

type Props = {
    x: number;
    y: number;
    width: number;
    height: number;
    payload: any;
    fill: string;
    dataKey: string;
    hoverState: { index: number | null; dataKey: string | null };
    barWidth?: number;
    month: string; // monthString prop is not used in this component, but kept for consistency with the original code
};

export const CustomBar = ({
    x,
    y,
    width,
    height,
    payload,
    fill,
    dataKey,
    hoverState,
    barWidth = 17,
    month: _month,
}: Props) => {
    const monthString = payload.monthString;
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ left: 0, top: 0 });

    const nowMonth = dayjs(_month).month() + 1; // 현재 월 (1월은 0, 12월은 11이므로 +1)
    //monthString 을 '월' 앞에까지 짤라서 숫자로 변환
    const month = Number(monthString.replace('월', ''));

    const isCurrentMonth = month === nowMonth;

    // index 는 이번달이 들어오면 3, 차례대로 이번달 -1 월은 2
    const index = month === nowMonth ? 3 : month === nowMonth - 1 ? 2 : month === nowMonth - 2 ? 1 : 0;

    const isHovered =
        hoverState.index !== null &&
        Number(hoverState.index) === Number(index) &&
        (hoverState.dataKey === null || hoverState.dataKey === dataKey);

    // 이번달은 무조건 dimmed 상태가 아니고, hoverState 가 있으면 dimmed 상태
    const isDimmed =
        month === nowMonth
            ? false
            : // otherwise, dim if there's a hover on some other bar
              (hoverState.index !== null && !isHovered) || (month !== nowMonth && !isHovered);
    const barColor = fill;
    function getRoundedTopRectPath(x: number, y: number, width: number, height: number, radius: number) {
        // width나 height가 0 이하일 때 기본값 설정
        const safeWidth = Math.max(0, width);
        const safeHeight = Math.max(0, height);
        const r = Math.min(3, safeWidth / 2, safeHeight); // 반지름이 너무 크면 깨지므로 제한

        return `
    M${x},${y + safeHeight}
    V${y + r}
    A${r},${r},0,0,1,${x + r},${y}
    H${x + safeWidth - r}
    A${r},${r},0,0,1,${x + safeWidth},${y + r}
    V${y + safeHeight}
    Z
  `;
    }
    const name =
        dataKey === 'centerReportWriteRate'
            ? 'SvgCenterStatisticsFeedWritingPercent'
            : dataKey === 'centerSatisfactionRate'
            ? 'SvgCenterStatisticsGoodPercent'
            : dataKey === 'centerReRegistrationSuccessRate'
            ? 'SvgCenterStatisticsReregisterSuccess'
            : 'SvgCenterStatisticsReregister';
    const color =
        dataKey === 'centerReportWriteRate'
            ? '#E6EEFF'
            : dataKey === 'centerSatisfactionRate'
            ? '#FFF0AE'
            : dataKey === 'centerReRegistrationSuccessRate'
            ? '#DDF7EF'
            : '#A32BFF40';
    const borderColor =
        dataKey === 'centerReportWriteRate'
            ? '#94B4F4'
            : dataKey === 'centerSatisfactionRate'
            ? '#FFD472'
            : dataKey === 'centerReRegistrationSuccessRate'
            ? '#8CD8C2'
            : '#E1BAFF';

    const fillColor =
        dataKey === 'centerReportWriteRate'
            ? colors.primary
            : dataKey === 'centerSatisfactionRate'
            ? '#FFB936'
            : dataKey === 'centerReRegistrationSuccessRate'
            ? '#169873'
            : '#C170FF';

    return (
        <>
            <circle
                cx={x + barWidth / 2}
                cy={y - 16}
                r={10}
                fill={color}
                stroke={borderColor}
                strokeWidth={0.25}
                opacity={isDimmed ? 0.4 : 1}
            />
            <SvgIcon
                name={name}
                size={10}
                fill={fillColor}
                x={x + barWidth / 2 - 5}
                y={y - 21}
                opacity={isDimmed ? 0.4 : 1}
            />
            {/* 아이콘 위에 텍스트 (값) */}
            {isCurrentMonth && (
                <text
                    x={x + barWidth / 2}
                    y={y - 40}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={12}
                    fill={'#4B545F'}
                    fontWeight="400"
                    letterSpacing={-0.5}
                >
                    {isZeroOrDefined(payload[dataKey]) ? payload[dataKey] + '%' : '-'}
                </text>
            )}

            <path
                d={getRoundedTopRectPath(x, y, barWidth, height, 6)}
                fill={barColor}
                style={{
                    cursor: 'pointer',
                    opacity: isDimmed ? 0.4 : 1,
                    transition: 'opacity 0.3s ease',
                }}
            />
        </>
    );
};
